const VALID_FILE_TYPES = ['xls', 'pdf', 'png', 'jpg', 'jpeg', 'xlsx'];
export const getIsValidFiletype = (fileName = '') => {
  return VALID_FILE_TYPES.some((type) => fileName.toLowerCase().endsWith(type));
};

//MRN numbers should be in the format of:
//Two numbers representing the year, Two letters representing the country code, 14 alphanumeric characters
//e.g. 24IT1234567890ABCD.
const MRN_REGEX = {
  ANY: /^[0-9]{2}[A-Z]{2}[0-9A-Z]{14}$/,
  GB: /^[0-9]{2}GB[0-9A-Z]{14}$/,
  FR: /^[0-9]{2}FR[0-9A-Z]{14}$/,
  IT: /^[0-9]{2}IT[0-9A-Z]{14}$/,
  IE: /^[0-9]{2}IE[0-9A-Z]{14}$/,
};

//GMR then 9 alphanumeric
const GMR_REGEX = /^GMR[A-Z0-9]{9}$/;

//8 alphanumeric
const PBN_REGEX = /^[A-Z0-9]{8}$/;

const EU_COUNTRY_CODE_REGEX = /(GB|AT|BE|BG|HR|CY|CZ|DK|EE|FI|FR|DE|GR|HU|IE|IT|LV|LT|LU|MT|NL|PL|PT|RO|SK|SI|ES|SE)/
  .source;
const CHED_TYPES_REGEX = /CHED(P|(PP)|D|A)\./.source;
const CHED_END_REGEX = /\.[0-9]{4}\.[0-9]{7}/.source;

//start with CHED followed by P, PP, D or A . 2 letters . 4 Numbers .  7 numbers
//eg CHEDP.GB.2025.1234567 CHEDPP.GB.2025.1234567 CHEDD.GB.2025.1234567 CHEDA.GB.2025.1234567
const CHED_REGEX = {
  GB: new RegExp(`^${CHED_TYPES_REGEX}GB${CHED_END_REGEX}$`),
  EU: new RegExp(`^${CHED_TYPES_REGEX}${EU_COUNTRY_CODE_REGEX}${CHED_END_REGEX}$`),
};

//IND2.GB. 4 digits . 7 digits
const PHILIS_REGEX = /IND2\.GB\.[0-9]{4}\.[0-9]{7}/;

/**
 * Checks whether a value is in a countries MRN format e.g. 27IT1234567890ABCD
 *
 * @param value - the string to test
 * @param {(string|string[])} countryOrCountries - A countryCode or array of country codes.
 */
export const getIsValidMrn = (value, countryOrCountries, caseSensitive = true) => {
  if (typeof value !== 'string') {
    return false;
  }

  //If we have an array of countries check all of them
  if (Array.isArray(countryOrCountries)) {
    return countryOrCountries.some((country) => {
      return getIsValidMrn(value, country);
    });
  }

  const regexp = countryOrCountries ? MRN_REGEX[countryOrCountries] : MRN_REGEX.ANY;
  if (!regexp) {
    console.warn('MRN validation country not found');
    return false;
  }

  if (caseSensitive) {
    return regexp.test(value);
  }

  const caseInsensitiveRegex = new RegExp(regexp.source, 'i');
  return caseInsensitiveRegex.test(value);
};

export const getIsValidGmr = (value) => {
  return typeof value === 'string' && GMR_REGEX.test(value);
};

export const getIsValidPbn = (value) => {
  return typeof value === 'string' && PBN_REGEX.test(value);
};

export const getIsValidEuChed = (value) => {
  return typeof value === 'string' && CHED_REGEX.EU.test(value);
};

export const getIsValidGbChed = (value) => {
  return typeof value === 'string' && CHED_REGEX.GB.test(value);
};

export const getIsValidPhilis = (value) => {
  return typeof value === 'string' && PHILIS_REGEX.test(value);
};

export const getIsValidJiraTicket = (string) => {
  if (typeof string !== 'string') return false;
  return /^CCC-\d{7}$/i.test(string);
};

export const TRANSIT_MRN_VALIDATION_MESSAGE = 'Transit MRN must be a valid MRN';

/** QA Preference Direct Transport Rules: valid MRN (2 digits + 2 letters + 14 alphanumeric) or NA. */
export const getIsValidPreferenceTransitMrn = (value) => {
  if (value == null || typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.toUpperCase() === 'NA') return true;

  return getIsValidMrn(trimmed.toUpperCase());
};

const CHED_DOUBLE_P_PREFIX = 'CHEDPP.';
const CHED_SINGLE_PREFIXES = ['CHEDP.', 'CHEDD.', 'CHEDA.'];
const CHED_DOUBLE_P_MAX_LENGTH = 22;
const CHED_SINGLE_MAX_LENGTH = 21;

/**
 * Cleanses a CHED reference as the user types/pastes it.
 * - Uppercases and strips whitespace.
 * - CHEDPP. -> max 22 chars
 * - CHEDP., CHEDD., CHEDA. -> max 21 chars
 * - All other input -> max 22 chars
 */
export const cleanseChedRef = (value) => {
  if (typeof value !== 'string') return value;
  const cleansed = value.toUpperCase().replace(/^\s+/, '').replace(/\s/g, '');

  if (cleansed.startsWith(CHED_DOUBLE_P_PREFIX)) {
    return cleansed.slice(0, CHED_DOUBLE_P_MAX_LENGTH);
  }
  if (CHED_SINGLE_PREFIXES.some((prefix) => cleansed.startsWith(prefix))) {
    return cleansed.slice(0, CHED_SINGLE_MAX_LENGTH);
  }

  return cleansed.slice(0, CHED_DOUBLE_P_MAX_LENGTH);
};
